'use client';

/**
 * <BrandedLoader> — full-screen branded loading state.
 *
 * Used on:
 *   - app/loading.tsx (Next.js root-segment fallback during navigation
 *     between authed and unauthed surfaces)
 *   - app/login/page.tsx (shown when an authenticated user lands on
 *     /login, suppresses the login form so the user never flashes the
 *     unauthed UI)
 *
 * Closes the "home page briefly shows the login page even when logged
 * in" tester report (2026-05-08). Pre-fix, an authed user navigating
 * to / saw:
 *   1. server-side redirect /  → /login (HTTP 307)
 *   2. /login renders the full login form during hydration (~200ms flash)
 *   3. useEffect reads the cookie and redirects to /recipients
 * Post-fix, app/page.tsx's SSR redirect routes the right destination
 * directly, AND any visible loading window shows the branded loader
 * (logo + spinner + rotating security tip) instead of the login form.
 */

import { useEffect, useState } from 'react';
import Image from 'next/image';

/**
 * Curated security tips. Rotated every ~3.5s while the loader is visible.
 * Keep the list short and high-quality — these double as user education.
 */
const SECURITY_TIPS = [
  'Phishing emails often pressure you to act fast. Pause before clicking.',
  'Hover over links before clicking — the visible text can hide the real URL.',
  'Use a unique password per service. A password manager makes this easy.',
  'Enable multi-factor authentication wherever it is offered.',
  'Report suspicious emails using the Phish Reporter button — every report helps.',
  'Software updates patch security holes. Install them as soon as they appear.',
  'A locked screen takes one keystroke. Step away → Win+L (or Cmd+Ctrl+Q).',
  'Verify unexpected payment requests by phone before transferring funds.',
  "If something feels off, it probably is. Trust your instinct — and double-check.",
  'Public Wi-Fi is convenient and risky. Use a VPN when you cannot avoid it.',
];

function pickRandom(): string {
  return SECURITY_TIPS[Math.floor(Math.random() * SECURITY_TIPS.length)];
}

export function BrandedLoader() {
  // Must be deterministic for the first render: Math.random() in a lazy
  // useState initializer runs independently on the server and the client,
  // so a random pick mismatches SSR vs hydration. React then discards and
  // regenerates the subtree, which churns every radix useId in the
  // surrounding shell. Start at a fixed tip, then randomize once mounted.
  const [tip, setTip] = useState<string>(SECURITY_TIPS[0]);

  useEffect(() => {
    setTip(pickRandom());
    const interval = setInterval(() => {
      setTip((current) => {
        // Avoid showing the same tip twice in a row.
        let next = current;
        let attempts = 0;
        while (next === current && attempts < 5) {
          next = SECURITY_TIPS[Math.floor(Math.random() * SECURITY_TIPS.length)];
          attempts += 1;
        }
        return next;
      });
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-8 bg-background px-6">
      <div className="flex flex-col items-center gap-4">
        {/* Light/dark pair. The old single asset was the white-ink wordmark
            rendered straight onto bg-background, so in light mode the
            "Human" half was white-on-white and simply invisible. */}
        <Image
          src="/brand/humanfirewall-ai-beta-light.svg"
          alt="HumanFirewall AI beta"
          width={259}
          height={40}
          priority
          className="block h-10 w-auto dark:hidden"
        />
        <Image
          src="/brand/humanfirewall-ai-beta.svg"
          alt="HumanFirewall AI beta"
          width={259}
          height={40}
          priority
          className="hidden h-10 w-auto dark:block"
        />
        <div className="h-2 w-48 overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-[loading-bar_1.6s_ease-in-out_infinite] rounded-full bg-primary" />
        </div>
      </div>

      <div className="max-w-md text-center" aria-live="polite">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Security Tip
        </p>
        <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
          {tip}
        </p>
      </div>

      <span className="sr-only">Loading…</span>

      <style jsx global>{`
        @keyframes loading-bar {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(300%);
          }
        }
      `}</style>
    </div>
  );
}
